import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, Map, RefreshCw, ShieldAlert, CloudOff, Cloud, Loader2, ChevronLeft } from "lucide-react";
import { useField } from "./FieldContext";
import InstallBanner from "./InstallBanner";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.18em] ${className}`}>{children}</span>
);

export default function FieldApp() {
  const { online, syncing, pending, org, sync } = useField();
  const nav = useNavigate();

  const tabs = [
    { to: "/field", icon: Home, label: "Events", end: true, testid: "tab-events" },
    { to: "/field/map", icon: Map, label: "Map", testid: "tab-map" },
    { to: "/field/swms", icon: ShieldAlert, label: "SWMS", testid: "tab-swms" },
    { to: "/field/sync", icon: RefreshCw, label: "Sync", testid: "tab-sync", badge: pending },
  ];

  return (
    <div className="min-h-screen bg-navy-950 text-white flex flex-col max-w-md mx-auto relative" data-testid="field-app">
      <header className="sticky top-0 z-20 bg-navy-900/95 backdrop-blur border-b border-gold-500/20 px-4 py-3 flex items-center justify-between">
        <button onClick={() => nav(-1)} data-testid="field-back" className="text-white/60 hover:text-gold-300 -ml-1 p-1">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="text-center leading-tight">
          <Mono className="text-gold-400 text-[0.6rem]">FieldClaim · MDAu</Mono>
          <div className="text-xs text-white/70 truncate max-w-[180px]" data-testid="field-org-name">{org?.name || "Not connected"}</div>
        </div>
        <button onClick={sync} disabled={syncing} data-testid="field-sync-btn" className="flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-sm border border-gold-500/25">
          {syncing ? <Loader2 className="h-4 w-4 animate-spin text-gold-300" /> : online ? <Cloud className="h-4 w-4 text-emerald-400" /> : <CloudOff className="h-4 w-4 text-amber-400" />}
          {pending > 0 && <span className="text-amber-300" data-testid="field-pending-count">{pending}</span>}
        </button>
      </header>

      {!online && (
        <div className="bg-amber-500/15 border-b border-amber-500/30 text-amber-200 text-[0.7rem] px-4 py-1.5 text-center" data-testid="field-offline-banner">
          Offline — captures are saved on this device and will sync automatically.
        </div>
      )}

      <InstallBanner />

      <main className="flex-1 px-4 py-5 pb-24 overflow-y-auto">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 inset-x-0 max-w-md mx-auto bg-navy-900/95 backdrop-blur border-t border-gold-500/20 grid grid-cols-4 z-20">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            data-testid={t.testid}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 py-2.5 text-[0.6rem] relative transition-colors ${isActive ? "text-gold-400" : "text-white/45"}`
            }
          >
            <t.icon className="h-5 w-5" />
            <Mono className="text-[0.55rem]">{t.label}</Mono>
            {t.badge > 0 && (
              <span className="absolute top-1.5 right-1/2 translate-x-4 bg-amber-400 text-navy-950 text-[0.55rem] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                {t.badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
